import { useEffect, RefObject } from "react"

type OutsideHandlerEvent = MouseEvent | TouchEvent | FocusEvent
type OutsideHandler = (event: OutsideHandlerEvent) => void

/**
 * Hook to set document event handlers when an element is clicked/touched/focused outside.
 * @param {*} ref React ref object
 * @param {Function} handler - function called when the event is triggered
 */
export default function useOutsideHandler<T extends HTMLElement>(
  ref: RefObject<T>,
  handler: OutsideHandler,
): void {
  useEffect(() => {
    const EVENTS = ["mousedown", "touchstart", "focusin"]

    const handleEvent = (event: Event) => {
      const target = event.target as Node | null
      if (!ref.current || !target) return
      if (!ref.current.contains(target)) {
        handler(event as OutsideHandlerEvent)
      }
    }

    EVENTS.forEach((event) => {
      document.addEventListener(event, handleEvent as EventListener)
    })

    return () => {
      EVENTS.forEach((event) => {
        document.removeEventListener(event, handleEvent as EventListener)
      })
    }
  }, [ref, handler])
}
